import { stripStyleUrlFunction } from '@common/html/util/style';
import { getTrackedChangesPluginDOMAttrs } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { nodeTrackedChangeAttrsToDOMAttrs } from '@common/util/schema';
import { syncDomAttrs } from '@portal-core/general/util/sync-dom-attrs';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class GlossaryTermNodeView extends NodeViewBase implements NodeView {
  /** A map of applied body attributes used to track attr syncing with syncDomAttrs. */
  private appliedBodyAttrs = new Map<string, any>();
  private beforeToggleListener: (event: ToggleEvent) => void;
  private buttonClickListener: (event: MouseEvent) => void;
  private buttonDOM: HTMLButtonElement;
  private expandingDOM: HTMLElement;
  private imgDOM: HTMLImageElement;
  private popupDOM: HTMLElement;

  /** Opt-in to sync the node when the stylesheet changes. */
  get syncNodeOnStylesheetChanges(): boolean {
    return true;
  }

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-glossaryterm-content'));
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-glossaryterm-button');
    this.imgDOM = this.buttonDOM.appendChild(document.createElement('img'));
    this.imgDOM.setAttribute('draggable', 'false');
    this.imgDOM.classList.add('mc-lwe-glossaryterm-image');

    this.popupDOM = document.createElement('mc-lwe-glossaryterm-popup');
    this.popupDOM.setAttribute('contenteditable', 'false');
    this.popupDOM.setAttribute('draggable', 'false');
    this.popupDOM.setAttribute('popover', 'auto');

    this.expandingDOM = document.createElement('mc-lwe-glossaryterm-expanding');
    this.expandingDOM.setAttribute('contenteditable', 'false');
    this.expandingDOM.setAttribute('draggable', 'false');

    // Listen for beforetoggle events to sync the node open state
    this.beforeToggleListener = (event) => {
      if (this.dom.contains(event.target as HTMLElement)) {
        if ((event as any).newState === 'open') {
          this.dom.setAttribute('mc-lwe-glossaryterm-open', '');
        } else {
          this.dom.removeAttribute('mc-lwe-glossaryterm-open');
        }

        this.syncNode(this.node, this.decorations);
      }
    };

    // The beforetoggle event does not bubble so use the capture phase
    this.dom.addEventListener('beforetoggle', this.beforeToggleListener, { capture: true });


    // Listen for the button click to toggle the open state when using the expanding display type
    this.buttonClickListener = () => {
      if (this.getDisplayType() === 'expanding') {
        if (this.dom.hasAttribute('mc-lwe-glossaryterm-open')) {
          this.dom.removeAttribute('mc-lwe-glossaryterm-open');
        } else {
          this.dom.setAttribute('mc-lwe-glossaryterm-open', '');
        }

        this.syncNode(this.node, this.decorations);
      }
    };

    this.buttonDOM.addEventListener('click', this.buttonClickListener);

    // The glossary term body (popup or expanding) is not ProseMirror-editable content,
    // so the base class position-aware navigation is sufficient for both display types.
    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode(this.node, this.decorations);
  }

  destroy() {
    this.dom.removeEventListener('beforetoggle', this.beforeToggleListener, { capture: true });
    this.buttonDOM.removeEventListener('click', this.buttonClickListener);
    super.destroy();
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Ignore toggling the open attribute
    if (mutation.type === 'attributes' && mutation.target === this.dom && mutation.attributeName === 'mc-lwe-glossaryterm-open') {
      return true;
    }

    return super.ignoreMutation(mutation);
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }

  syncNode(node: ProseMirrorNode, decorations: readonly Decoration[]) {
    // Grab the mc style for the glossary term display type
    const displayType = this.getDisplayType();

    // Update the node based on the display type
    if (displayType === 'popup') {
      this.syncNodeAsPopup(node, decorations);
    } else {
      this.syncNodeAsExpanding(node, decorations);
    }

    // Grab the mc styles for the glossary term image
    const closedImage = getComputedStyle(this.dom).getPropertyValue('--mc-closed-image') || 'url(assets/images/editor/flare/expandingclosed.gif)';
    const openImage = getComputedStyle(this.dom).getPropertyValue('--mc-open-image') || 'url(assets/images/editor/flare/expandingopen.gif)';
    const imageSpacing = getComputedStyle(this.dom).getPropertyValue('--mc-image-spacing') || '5px';
    const imagePosition = getComputedStyle(this.dom).getPropertyValue('--mc-image-position') || 'right';

    const isOpen = this.dom.hasAttribute('mc-lwe-glossaryterm-open');
    let imageSrc: string | null = isOpen ? stripStyleUrlFunction(openImage) : stripStyleUrlFunction(closedImage);
    if (imagePosition === 'none') {
      imageSrc = null;
    }

    // Update the img and button elements
    if (imageSrc) {
      this.imgDOM.src = imageSrc;

      if (imagePosition === 'left') {
        this.buttonDOM.style.removeProperty('margin-left');
        this.buttonDOM.style.setProperty('margin-right', imageSpacing);
        // Ensure the button is the first child
        if (this.buttonDOM !== this.dom.firstChild) {
          this.dom.prepend(this.buttonDOM);
        }
      } else if (imagePosition === 'right') {
        this.buttonDOM.style.setProperty('margin-left', imageSpacing);
        this.buttonDOM.style.removeProperty('margin-right');
        // Ensure the button is just after the contentDOM
        if (this.buttonDOM !== this.contentDOM.nextElementSibling) {
          this.contentDOM.insertAdjacentElement('afterend', this.buttonDOM);
        }
      }
    } else {
      this.imgDOM.removeAttribute('src');
      this.buttonDOM.style.removeProperty('margin-left');
      this.buttonDOM.style.removeProperty('margin-right');
    }
  }

  /**
   * Sync the node when the display type is 'expanding' by ensuring the expanding element is in place and the body content is updated accordingly, and updating the button popover properties to disable the popup behavior.
   */
  private syncNodeAsExpanding(node: ProseMirrorNode, decorations: readonly Decoration[]) {
    // Ensure the popup element is removed and the expanding element is in place
    this.popupDOM.remove();

    if (this.expandingDOM !== this.dom.lastChild) {
      this.dom.appendChild(this.expandingDOM);
    }

    // Update the body content based on the glossary term attribute
    this.updateBodyContent(node, decorations, this.expandingDOM);

    // Update the button popover properties to disable the popup behavior
    (this.buttonDOM as any).popoverTargetElement = null;
    (this.buttonDOM as any).popoverTargetAction = null;
  }

  /**
   * Sync the node when the display type is 'popup' by ensuring the popup element is in place and the body content and button popover properties are updated accordingly.
   */
  private syncNodeAsPopup(node: ProseMirrorNode, decorations: readonly Decoration[]) {
    // Ensure the expanding element is removed and the popup element is in place
    this.expandingDOM.remove();

    if (this.popupDOM !== this.dom.lastChild) {
      this.dom.appendChild(this.popupDOM);
    }

    // Update the body content based on the glossary term attribute
    this.updateBodyContent(node, decorations, this.popupDOM);

    // Update the button popover properties to enable the popup behavior
    (this.buttonDOM as any).popoverTargetElement = this.popupDOM;
    (this.buttonDOM as any).popoverTargetAction = 'toggle';
  }

  /**
   * Update the body content of the popup or expanding element based on the glossary term's glossTerm attribute.
   * If the attribute is missing, displays a message indicating the missing attribute.
   * @param bodyDOM The DOM element of the popup or expanding body to update the content of.
   */
  private updateBodyContent(node: ProseMirrorNode, decorations: readonly Decoration[], bodyDOM: HTMLElement) {
    const glossTerm = node.attrs['glossterm'];
    if (glossTerm) {
      bodyDOM.textContent = glossTerm;
    } else {
      bodyDOM.innerHTML = '<i>The glossary term\'s glossTerm attribute is missing.</i>';
    }

    // Add the glossary term's tracked change DOM attrs to the body so that tracked change styles are applied to the body as well
    syncDomAttrs(bodyDOM, {
      ...nodeTrackedChangeAttrsToDOMAttrs(node),
      ...getTrackedChangesPluginDOMAttrs(decorations)
    }, this.appliedBodyAttrs);
  }

  /**
   * Get the glossary term display type based on the --mc-term-display style, defaulting to 'popup' if the style is not set.
   * @returns The glossary term display type, either 'popup' or 'expanding'.
   */
  private getDisplayType(): 'popup' | 'expanding' {
    const displayType = getComputedStyle(this.dom).getPropertyValue('--mc-term-display') || 'popup';
    return displayType === 'popup' ? 'popup' : 'expanding';
  }
}
